/**
 * App Layout with navigation bar
 * Displays header with user info and navigation
 */

import { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  AdminPanelSettings,
  Description,
  Logout,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { UserRole } from '../types';
import { colors } from '../theme/theme';
import { getInitials, getAvatarColor, layout } from '../utils/designSystem';

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { mode, toggleTheme } = useThemeStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === UserRole.ADMIN;
  const currentPath = location.pathname;
  const userInitials = user?.email ? getInitials(user.email) : '?';
  const avatarColor = user?.email ? getAvatarColor(user.email) : colors.text.disabled;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <AppBar 
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: colors.background.paper,
          borderBottom: `1px solid ${colors.divider}`,
          height: layout.navHeight,
        }}
      >
        <Toolbar sx={{ height: '100%', minHeight: `${layout.navHeight}px !important` }}>
          {/* Logo/Title */}
          <Typography
            variant="h5"
            component="div"
            sx={{ 
              flexGrow: 0, 
              mr: 4, 
              cursor: 'pointer',
              fontWeight: 700,
              color: colors.text.primary,
            }}
            onClick={() => navigate('/')}
          >
            SPINE Tagging
          </Typography>

          {/* Navigation Links */}
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
            <Button
              color="inherit"
              onClick={() => navigate('/')}
              sx={{
                fontWeight: 500,
                color: currentPath === '/' ? colors.primary.main : colors.text.secondary,
                backgroundColor: currentPath === '/' ? colors.background.elevated : 'transparent',
                '&:hover': {
                  backgroundColor: colors.background.elevated,
                },
                borderRadius: '6px',
                px: 2,
              }}
            >
              Predictions
            </Button>
            {isAdmin && (
              <Button
                color="inherit"
                onClick={() => navigate('/admin')}
                startIcon={<AdminPanelSettings />}
                sx={{
                  fontWeight: 500,
                  color: currentPath === '/admin' ? colors.primary.main : colors.text.secondary,
                  backgroundColor: currentPath === '/admin' ? colors.background.elevated : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.background.elevated,
                  },
                  borderRadius: '6px',
                  px: 2,
                }}
              >
                Admin
              </Button>
            )}
            <Button
              color="inherit"
              onClick={() => navigate('/docs')}
              startIcon={<Description />}
              sx={{
                fontWeight: 500,
                color: currentPath === '/docs' ? colors.primary.main : colors.text.secondary,
                backgroundColor: currentPath === '/docs' ? colors.background.elevated : 'transparent',
                '&:hover': {
                  backgroundColor: colors.background.elevated,
                },
                borderRadius: '6px',
                px: 2,
              }}
            >
              Docs
            </Button>
          </Box>

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Theme Toggle */}
            <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: colors.background.elevated,
                  },
                }}
              >
                {mode === 'dark' ? <LightMode /> : <DarkMode />}
              </IconButton>
            </Tooltip>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.text.secondary,
                display: { xs: 'none', md: 'block' },
              }}
            >
              {user?.email}
            </Typography>
            <IconButton
              size="small"
              aria-label="account menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              sx={{
                p: 0.5,
                '&:hover': {
                  backgroundColor: colors.background.elevated,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: avatarColor,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {userInitials}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    minWidth: 200,
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text.primary }}>
                  {user?.email}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                  {user?.role}
                </Typography>
              </Box>
              <Divider />
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  gap: 1.5,
                  py: 1.5,
                  color: colors.text.primary,
                }}
              >
                <Logout fontSize="small" />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: `${layout.navHeight}px`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
